var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import classnames from "classnames";
import { scaleSequential } from "d3-scale";
import { interpolateSpectral } from "d3-scale-chromatic";
import Node, { NumberOfChildrenPlacement } from "../Node";
import Breadcrumb from "../Breadcrumb";
import { ColorModel } from "./TreeMapProps";
import TooltipProvider from "../Tooltip/TooltipProvider";
import { getColorsFromNode, getValueFormatFn } from "./helpers";
import { calculateTreeMap } from "./calculateTreeMap";
var TreeMap = function (props) {
    var _a = props.id, treemapId = _a === void 0 ? "myTreeMap" : _a, _b = props.data, data = _b === void 0 ? null : _b, _c = props.height, height = _c === void 0 ? 600 : _c, _d = props.width, width = _d === void 0 ? 600 : _d, _e = props.valueFormat, valueFormat = _e === void 0 ? ",d" : _e, _f = props.disableBreadcrumb, disableBreadcrumb = _f === void 0 ? false : _f, _g = props.colorModel, colorModel = _g === void 0 ? ColorModel.OneEachChildren : _g, _h = props.customD3ColorScale, customD3ColorScale = _h === void 0 ? scaleSequential(interpolateSpectral) : _h, _j = props.namePropInData, namePropInData = _j === void 0 ? "name" : _j, _k = props.linkPropInData, linkPropInData = _k === void 0 ? "link" : _k, _l = props.valuePropInData, valuePropInData = _l === void 0 ? "value" : _l, _m = props.childrenPropInData, childrenPropInData = _m === void 0 ? "children" : _m, _o = props.numberOfChildrenPlacement, numberOfChildrenPlacement = _o === void 0 ? NumberOfChildrenPlacement.None : _o, _p = props.darkNodeTextColor, darkNodeTextColor = _p === void 0 ? "white" : _p, _q = props.darkNodeBorderColor, darkNodeBorderColor = _q === void 0 ? "white" : _q, _r = props.lightNodeTextColor, lightNodeTextColor = _r === void 0 ? "black" : _r, _s = props.lightNodeBorderColor, lightNodeBorderColor = _s === void 0 ? "black" : _s, _t = props.disableTooltip, disableTooltip = _t === void 0 ? false : _t, _u = props.tooltipOffsetX, tooltipOffsetX = _u === void 0 ? 0 : _u, _v = props.tooltipOffsetY, tooltipOffsetY = _v === void 0 ? 0 : _v, _w = props.levelsToDisplay, levelsToDisplay = _w === void 0 ? 1 : _w, svgClassName = props.svgClassName, svgStyle = props.svgStyle, className = props.className, breadCrumbClassName = props.breadCrumbClassName, tooltipPlacement = props.tooltipPlacement, tooltipClassName = props.tooltipClassName, valueFn = props.valueFn, splitRegExp = props.splitRegExp, hideValue = props.hideValue, nodeClassName = props.nodeClassName, nodeStyle = props.nodeStyle, onZoom = props.onZoom, valueUnit = props.valueUnit, _x = props.paddingOuter, paddingOuter = _x === void 0 ? 1 : _x, _y = props.paddingInner, paddingInner = _y === void 0 ? 1 : _y, onNodeClick = props.onNodeClick;
    var originalTopNode = React.useMemo(function () {
        return calculateTreeMap({
            width: width,
            height: height,
            data: data,
            valuePropInData: valuePropInData,
            paddingOuter: paddingOuter,
            paddingInner: paddingInner,
        });
    }, [data, height, paddingInner, paddingOuter, valuePropInData, width]);
    var originalTopNodeColorsLookup = React.useMemo(function () {
        var lookup = {};
        originalTopNode.each(function (node) {
            lookup[node.data.id] = getColorsFromNode({
                node: node,
                originalTopNode: originalTopNode,
                childrenPropInData: childrenPropInData,
                colorModel: colorModel,
                customD3ColorScale: customD3ColorScale,
                valuePropInData: valuePropInData,
                darkNodeTextColor: darkNodeTextColor,
                darkNodeBorderColor: darkNodeBorderColor,
                lightNodeTextColor: lightNodeTextColor,
                lightNodeBorderColor: lightNodeBorderColor,
            });
        });
        return lookup;
    }, [
        childrenPropInData,
        colorModel,
        customD3ColorScale,
        darkNodeBorderColor,
        darkNodeTextColor,
        lightNodeBorderColor,
        lightNodeTextColor,
        originalTopNode,
        valuePropInData,
    ]);
    var _z = React.useState(originalTopNode), selectedNode = _z[0], setSelectedNode = _z[1];
    var zoomTo = React.useCallback(function (nodeId) {
        var currentNode = originalTopNode
            .descendants()
            .filter(function (item) {
            return item.data.id === nodeId;
        })
            .pop();
        if (currentNode && "data" in currentNode && currentNode.data) {
            if (onZoom) {
                onZoom(currentNode);
            }
            setSelectedNode(calculateTreeMap({
                width: width,
                height: height,
                data: currentNode.data,
                valuePropInData: valuePropInData,
                paddingOuter: paddingOuter,
                paddingInner: paddingInner,
            }));
        }
        else {
            console.warn("No node found for " + currentNode.name);
        }
    }, [
        height,
        onZoom,
        originalTopNode,
        paddingInner,
        paddingOuter,
        valuePropInData,
        width,
    ]);
    var handleNodeClick = React.useCallback(function (node) {
        return onNodeClick
            ? function () {
                onNodeClick(node);
            }
            : undefined;
    }, [onNodeClick]);
    var renderNode = React.useCallback(function (node) {
        var data = node.data, x0 = node.x0, x1 = node.x1, y0 = node.y0, y1 = node.y1;
        var id = data.id;
        var name = data[namePropInData];
        var url = data[linkPropInData];
        var nodeClassNameFromData = data["className"];
        var hasChildren = node[childrenPropInData] && node[childrenPropInData].length > 0
            ? true
            : false;
        var formatted = node[valuePropInData];
        try {
            formatted = getValueFormatFn(valueFn, valueFormat)(node[valuePropInData]);
        }
        catch (e) {
            console.warn(e);
        }
        var formattedValue = "(".concat(formatted).concat(valueUnit ? " ".concat(valueUnit) : "", ")");
        var nodeTotalNodes = node.descendants().length - 1;
        var _a = originalTopNodeColorsLookup[id], bgColor = _a.bgColor, textColor = _a.textColor, borderColor = _a.borderColor, textColorBorderColorBg = _a.textColorBorderColorBg;
        var isSelectedNode = id === selectedNode.data.id;
        var selectedNodeFromOriginalTree = originalTopNode
            .descendants()
            .filter(function (n) {
            return n.data.id === selectedNode.data.id;
        })
            .pop();
        return (React.createElement(Node, { bgColor: bgColor, textColor: textColor, borderColor: borderColor, textColorBorderColorBg: textColorBorderColorBg, className: classnames(nodeClassName, nodeClassNameFromData), style: __assign({ fontVariant: "normal", fontWeight: "normal", fontSize: 14, fontFamily: "Arial" }, nodeStyle), hasChildren: hasChildren, id: id, isSelectedNode: isSelectedNode, key: id, label: name, nodeTotalNodes: nodeTotalNodes, onClick: handleNodeClick(node), onClickDrillDown: !isSelectedNode && hasChildren
                ? function () { return zoomTo(node.data.id); }
                : undefined, onClickBack: isSelectedNode && selectedNodeFromOriginalTree.parent
                ? function () { return zoomTo(selectedNodeFromOriginalTree.parent.data.id); }
                : undefined, treemapId: treemapId, url: url, value: !hideValue && formattedValue, x0: x0, x1: x1, y0: y0, y1: y1, numberOfChildrenPlacement: numberOfChildrenPlacement, splitRegExp: splitRegExp }));
    }, [
        childrenPropInData,
        handleNodeClick,
        hideValue,
        linkPropInData,
        namePropInData,
        nodeClassName,
        nodeStyle,
        numberOfChildrenPlacement,
        originalTopNode,
        originalTopNodeColorsLookup,
        selectedNode.data.id,
        splitRegExp,
        treemapId,
        valueFn,
        valueFormat,
        valuePropInData,
        valueUnit,
        zoomTo,
    ]);
    var reactNodes = React.useMemo(function () {
        var nodes = [];
        var maxLevel = selectedNode.depth === 0 ? levelsToDisplay : 1;
        var iterateAllChildren = function (mainNode, level) {
            nodes = nodes.concat(renderNode(mainNode));
            if (level < maxLevel) {
                if (childrenPropInData in mainNode &&
                    mainNode[childrenPropInData].length > 0) {
                    mainNode[childrenPropInData].forEach(function (element) {
                        iterateAllChildren(element, level + 1);
                    });
                }
            }
        };
        iterateAllChildren(selectedNode, 0);
        return nodes;
    }, [childrenPropInData, levelsToDisplay, renderNode, selectedNode]);
    return (React.createElement(TooltipProvider, { tooltipPlacement: tooltipPlacement, tooltipClassName: tooltipClassName, disableTooltip: disableTooltip, tooltipOffsetX: tooltipOffsetX, tooltipOffsetY: tooltipOffsetY },
        React.createElement("div", { className: className },
            disableBreadcrumb === false ? (React.createElement(Breadcrumb, { originalTopNode: originalTopNode, selectedNodeId: selectedNode.data.id, zoomTo: zoomTo, className: breadCrumbClassName, namePropInData: namePropInData })) : null,
            React.createElement("svg", { className: classnames(svgClassName), height: height, width: width, style: __assign({}, svgStyle) }, reactNodes))));
};
export default TreeMap;
